import React, { Fragment } from "react";

import {
    BodyBlock,
    CTABlock,
    CTAGroupBlock,
    EyebrowOptionalBlock,
    HeadingBlock,
    ModalCTABlock,
    RichTextImageBlock,
    RichTextStreamBlockOrNull as Value,
} from "@reactivated";

import { constants } from "@reactivated/constants";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { assertNever } from "@thelabnyc/thelabui/src/utils/never";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ButtonPrimary, ButtonSecondary } from "../Clickables";
import { RichText } from "../RichText";
import { ScheduleVisitModal } from "../ScheduleVisitModal";

import styles from "./index.module.scss";

const ContentSize = constants["server.cms.types.ContentSize"];
export const contentSizeScale = Object.values(ContentSize);
type ContentSizeValue = (typeof contentSizeScale)[number];

/**
 * Resolve final content size by applying adjustment to base size.
 */
const resolveContentSize = (
    base: ContentSizeValue,
    adjustment: number,
): ContentSizeValue => {
    const index = contentSizeScale.indexOf(base);
    const adjusted = Math.max(
        0,
        Math.min(contentSizeScale.length - 1, index + adjustment),
    );
    return contentSizeScale[adjusted];
};

/**
 * Strip <p> wrappers from richtext HTML for valid HTML inside headings.
 * Draftail always wraps content in <p> tags, but <p> inside <h1> is invalid.
 * Multiple paragraphs are joined with <br />.
 */
export const stripParagraphWrapper = (html: string): string => {
    const matches = [...html.matchAll(/<p[^>]*>([\s\S]*?)<\/p>/g)];
    if (matches.length > 0) {
        return matches.map((m) => m[1]).join("<br />");
    }
    return html;
};

/** The DOM id of the first heading block, for `aria-labelledby` — the heading
 * renders with `id={block.id}`. */
export const firstHeadingId = (value: Value): string | undefined =>
    value?.content?.find((block) => block.type === "heading")?.id;

interface EyebrowProps {
    id?: string;
    size?: "small" | "large";
    value: EyebrowOptionalBlock;
}

export const Eyebrow = ({ id, size = "large", value }: EyebrowProps) => {
    const Tag = value.tag ?? "p";
    return (
        <Tag
            id={id}
            className={concatClassNames([
                size === "small" ? styles.eyebrowSmall : styles.eyebrowLarge,
                value.color === "accent" ? styles.eyebrowAccent : "",
            ])}
        >
            {value.text}
        </Tag>
    );
};

export const Heading = ({
    id,
    className,
    value,
}: {
    id?: string;
    className?: string;
    value: HeadingBlock;
}) => {
    const html = stripParagraphWrapper(value.text ?? "");
    if (!html) return null;
    const Tag = value.tag ?? "h2";
    return (
        <Tag
            id={id}
            className={concatClassNames([styles.heading, className])}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

const Body = ({ value }: { value: BodyBlock }) => {
    if (!value.content) return null;
    return (
        <RichText
            value={value.content}
            constrainParagraphWidth={value.width}
            className={styles.body}
        />
    );
};

const CTA = ({ value }: { value: CTABlock }) => {
    if (!value.link || !value.link.url) return null;
    const Button =
        value.style === "secondary" ? ButtonSecondary : ButtonPrimary;
    return (
        <Button
            href={value.link.url}
            aria-label={value.aria_label || undefined}
        >
            {value.link.display_name}
        </Button>
    );
};

const ModalCTA = ({ value }: { value: ModalCTABlock }) => {
    const modal = value.modal?.[0];
    if (!value.text || !modal) return null;
    const Button =
        value.style === "secondary" ? ButtonSecondary : ButtonPrimary;
    const trigger = (
        <Button aria-label={value.aria_label || undefined}>{value.text}</Button>
    );
    switch (modal.type) {
        case "schedule_visit":
            return <ScheduleVisitModal trigger={trigger} />;
        default:
            return assertNever(modal.type);
    }
};

const ctaComponents = {
    item: CTA,
    modal_cta: ModalCTA,
};

const CTAGroup = ({ value }: { value: CTAGroupBlock }) => {
    if (!value.ctas || value.ctas.length === 0) return null;
    return (
        <div className={styles.ctaGroup}>
            <StreamField
                tagName={Fragment}
                components={ctaComponents}
                value={value.ctas}
            />
        </div>
    );
};

const Image = ({ value }: { value: RichTextImageBlock }) => {
    if (!value.image) return null;
    const style: React.CSSProperties = {
        "--image-width": `${value.width ?? 100}%`,
    };
    if (value.max_width_px) {
        style["--image-max-width"] = `${value.max_width_px}px`;
    }
    return (
        <ImageChooserBlock
            value={value.image}
            attrs={{ className: styles.imageBlock, style }}
            sizes={
                value.max_width_px
                    ? `${value.max_width_px}px`
                    : `${value.width ?? 100}vw`
            }
        />
    );
};

const richTextStreamBlockComponents = {
    eyebrow: Eyebrow,
    heading: Heading,
    body: Body,
    cta_group: CTAGroup,
    image: Image,
};

// "none" is a valid lock option on the backend but isn't reflected in the
// generated types because it's excluded from the editor-facing choices.
type AdjustedValue =
    | (Omit<NonNullable<Value>, "alignment" | "container_width"> & {
          alignment: NonNullable<Value>["alignment"] | "none";
          container_width: NonNullable<Value>["container_width"] | "none";
      })
    | null;

/**
 * Map a container_width value to a grid column count (created with HeroLayout's
 * contentWidth in mind).
 * @param width look up ContainerWidth TextChoices for expected values
 * @param fallback 1 to 12 columns
 * @returns a number that represents a column width
 */
export const containerWidthToColumns = (
    width: NonNullable<Value>["container_width"] | "none" | undefined,
    fallback = 12,
): number => {
    const match = width?.match(/^width(\d+)$/);
    return match ? parseInt(match[1], 10) : fallback;
};

interface Props {
    value: AdjustedValue;
    className?: string;
    hideBodyOnMobile?: boolean;
}

export const RichTextStreamBlock = ({
    value,
    className,
    hideBodyOnMobile,
}: Props) => {
    if (!value) return <></>;
    const {
        content,
        content_size,
        size_adjustment,
        alignment,
        container_width,
    } = value;
    if (!content || content.length === 0) return <></>;

    const resolvedSize = resolveContentSize(
        content_size ?? ContentSize.L,
        parseInt(size_adjustment ?? "0", 10) || 0,
    );

    const hasGrid = container_width && container_width !== "none";

    return (
        <div
            data-block="RichTextStreamBlock"
            className={hasGrid ? styles.gridContainer : styles.contents}
        >
            <div
                className={concatClassNames([
                    styles.content,
                    styles[resolvedSize],
                    alignment ? styles[alignment] : "",
                    hasGrid ? styles[container_width] : "",
                    hasGrid ? styles.gridInner : "",
                    hideBodyOnMobile ? styles.hideBodyOnMobile : "",
                    className,
                ])}
            >
                <StreamField
                    tagName={Fragment}
                    components={richTextStreamBlockComponents}
                    value={content}
                />
            </div>
        </div>
    );
};
